/**
 * Utility functions for formatting API error responses.
 */

interface PydanticValidationError {
  type: string;
  loc: (string | number)[];
  msg: string;
  input?: any;
  ctx?: Record<string, any>;
}

/**
 * Format a Pydantic validation error for display.
 * 
 * Pydantic returns 422 errors in this format:
 * {
 *   detail: [
 *     {
 *       type: "greater_than_equal",
 *       loc: ["body", "steps", 1, "order"],
 *       msg: "Input should be greater than or equal to 0",
 *       input: -1,
 *       ctx: { ge: 0 }
 *     }
 *   ]
 * }
 * 
 * @param error - The error object from axios
 * @returns A human-readable error message
 */
export function formatApiError(error: any): string {
  // Check if it's an axios error with response
  if (!error.response?.data) {
    return error.message || 'An unknown error occurred';
  }

  const detail = error.response.data.detail;

  // Case 1: detail is a string (standard FastAPI error)
  if (typeof detail === 'string') {
    return detail;
  }

  // Case 2: detail is an array of Pydantic validation errors
  if (Array.isArray(detail)) {
    return formatPydanticErrors(detail);
  }

  // Case 3: detail is an object (shouldn't happen but handle it)
  if (typeof detail === 'object') {
    return JSON.stringify(detail);
  }

  // Fallback
  return 'Failed to validate request';
}

/**
 * Format an array of Pydantic validation errors into a readable message.
 * 
 * @param errors - Array of Pydantic validation errors
 * @returns A formatted error message
 */
function formatPydanticErrors(errors: PydanticValidationError[]): string {
  if (errors.length === 0) {
    return 'Validation failed';
  }

  if (errors.length === 1) {
    return formatSinglePydanticError(errors[0]);
  }

  // Multiple errors - format as a list
  const errorMessages = errors.map((err, index) => 
    `${index + 1}. ${formatSinglePydanticError(err)}`
  );

  return `Validation failed:\n${errorMessages.join('\n')}`;
}

/**
 * Format a single Pydantic validation error.
 * 
 * @param error - A Pydantic validation error object
 * @returns A formatted error message
 */
function formatSinglePydanticError(error: PydanticValidationError): string {
  // Extract the field path (skip "body" which is always first)
  const fieldPath = error.loc
    .slice(1) // Skip "body"
    .map((part) => {
      if (typeof part === 'number') {
        return `[${part}]`;
      }
      return part;
    })
    .join('.');

  // Build a readable message
  let message = error.msg;

  // Add field context if available
  if (fieldPath) {
    message = `${fieldPath}: ${message}`;
  }

  // Add input value if available and not too long
  if (error.input !== undefined) {
    const inputStr = JSON.stringify(error.input);
    if (inputStr.length < 50) {
      message += ` (received: ${inputStr})`;
    }
  }

  return message;
}

